import React, { useState } from "react";
import { withApollo } from "react-apollo";
import queries from "../../../queries";
import ImageUploader from "../../uploaders/ImageUploader";
import HtmlDescriptionEditor from "./HtmlDescriptionEditor";

// Manual editing of the items of a Produits 2.0 cell (speakers, exhibitors...): search, add, edit, delete.
// The same items can be replaced in bulk with the CSV import of the cell.

const compare = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

function ItemForm({ item, categories, open, onToggle, onSave, onDelete }) {
  const [form, setForm] = useState(() => ({
    name: item.name || '',
    subtitle: item.subtitle || '',
    category: item.category || '',
    description: item.description || '',
    image_uri: item.image_uri || '',
  }));
  const [state, setState] = useState('');
  const set = (key, value) => { setForm((previous) => ({ ...previous, [key]: value })); setState(''); };

  const save = async () => {
    setState('Enregistrement…');
    try {
      await onSave({ id: item.id, ...form });
      setState('Enregistré');
    } catch (error) {
      setState('Erreur : ' + error.message);
    }
  };

  return (
    <div style={{ border: '1px solid var(--tsk-border)', borderRadius: 4, marginBottom: 8, background: 'var(--tsk-bg-card)', color: 'var(--tsk-text)' }}>
      <div onClick={onToggle} style={{ cursor: 'pointer', padding: '8px 10px', display: 'flex', gap: 10, alignItems: 'center' }}>
        {item.image_uri && <img src={item.image_uri} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }} />}
        <strong style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name || '(sans nom)'}</strong>
        <span style={{ opacity: 0.6 }}>{item.category}</span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </div>
      {open &&
        <div style={{ padding: '4px 10px 10px' }}>
          <div className="tsk-input-line">
            <label>Nom</label>
            <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} />
          </div>
          <div className="tsk-input-line">
            <label>Sous-titre (fonction)</label>
            <input type="text" value={form.subtitle} onChange={(e) => set('subtitle', e.target.value)} />
          </div>
          <div className="tsk-input-line">
            <label>Catégorie (onglet)</label>
            <input type="text" list={`products2-categories-${item.cell_id}`} value={form.category} placeholder="Ex. : A ou B-C" onChange={(e) => set('category', e.target.value)} />
            <datalist id={`products2-categories-${item.cell_id}`}>
              {categories.map((category) => <option key={category} value={category} />)}
            </datalist>
          </div>
          <div className="tsk-input-line tsk-input-line--full">
            <label>Photo / logo</label>
            <ImageUploader
              value={form.image_uri}
              canDelete
              onUpload={(uri) => set('image_uri', uri)}
              onDelete={() => set('image_uri', '')}
            />
          </div>
          <div className="tsk-input-line">
            <label>Description du popup</label>
            <HtmlDescriptionEditor value={form.description} onChange={(html) => set('description', html)} />
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
            <button type="button" className="tsk-button" onClick={save}>Enregistrer</button>
            <button
              type="button" className="tsk-button"
              onClick={() => { if (window.confirm(`Supprimer « ${item.name} » ?`)) onDelete(); }}
            >
              Supprimer
            </button>
            <span>{state}</span>
          </div>
        </div>
      }
    </div>
  );
}

function Products2ItemsEditor({ cell, refetch, client }) {
  const [openId, setOpenId] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const categories = [...new Set(cell.items.map((item) => item.category || '').filter(Boolean))].sort(compare);
  const needle = search.trim().toLowerCase();
  const items = [...cell.items]
    .filter((item) => !categoryFilter || (item.category || '') === categoryFilter)
    .filter((item) => !needle || (item.name || '').toLowerCase().includes(needle) || (item.subtitle || '').toLowerCase().includes(needle))
    .sort((a, b) => compare(a.name || '', b.name || ''));

  const run = async (mutation, variables) => {
    await client.mutate({ mutation, variables });
    await refetch();
  };

  const add = async () => {
    const result = await client.mutate({
      mutation: queries.cells.products2.addItem,
      variables: { input: { cell_id: cell.id, name: 'Nouvel élément', category: categoryFilter } },
    });
    await refetch();
    setSearch('');
    setOpenId(result.data.addProducts2Item.id);
  };

  return (
    <div>
      <p style={{ margin: '0 0 8px' }}>{cell.items.length} élément(s) au total{categories.length > 0 ? `, ${categories.length} catégorie(s)` : ''}. Triés par ordre alphabétique.</p>
      <div className="tsk-input-line">
        <label>Rechercher</label>
        <input type="text" value={search} placeholder="Nom ou sous-titre" onChange={(e) => setSearch(e.target.value)} />
      </div>
      {categories.length > 0 &&
        <div className="tsk-input-line">
          <label>Catégorie</label>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="">Toutes</option>
            {categories.map((category) => <option key={category} value={category}>{category}</option>)}
          </select>
        </div>
      }
      {items.length === 0 && <p>Aucun élément{needle || categoryFilter ? ' pour ce filtre' : ' pour l\'instant : importe un CSV ou ajoute-en un à la main'}.</p>}
      {items.map((item) => (
        <ItemForm
          key={[item.id, item.name, item.subtitle, item.category, item.description, item.image_uri].join('|')}
          item={item}
          categories={categories}
          open={openId === item.id}
          onToggle={() => setOpenId(openId === item.id ? null : item.id)}
          onSave={(input) => run(queries.cells.products2.updateItem, { input })}
          onDelete={() => run(queries.cells.products2.deleteItem, { id: item.id })}
        />
      ))}
      <button type="button" className="tsk-button" onClick={add}>Ajouter un élément</button>
    </div>
  );
}

export default withApollo(Products2ItemsEditor);
